import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Rocket, TrendingUp, Users, Activity, ArrowRight } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface OnboardingCompleteProps {
  onComplete: () => void;
  onBack: () => void;
  playersAdded: number;
  dataUploaded: string[];
}

export default function OnboardingComplete({
  onComplete,
  onBack,
  playersAdded,
  dataUploaded,
}: OnboardingCompleteProps) {
  const { translate: t } = useTranslation();

  return (
    <div className="text-center space-y-8">
      <div className="inline-flex p-4 bg-primary/10 rounded-full">
        <Rocket className="h-12 w-12 text-primary" />
      </div>

      <div className="space-y-4">
        <h1 className="text-4xl md:text-5xl font-bold">{t("onboarding_complete_title")}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {t("onboarding_complete_desc")}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6 space-y-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold mb-1">{t("onboarding_complete_team_setup")}</h3>
              <p className="text-2xl font-bold text-primary">{playersAdded}</p>
              <p className="text-sm text-muted-foreground">
                {playersAdded !== 1
                  ? t("onboarding_complete_players_added_plural")
                  : t("onboarding_complete_players_added")}
              </p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-primary" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6 space-y-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Activity className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold mb-1">{t("onboarding_complete_data_uploaded")}</h3>
              <p className="text-2xl font-bold text-primary">{dataUploaded.length}</p>
              <p className="text-sm text-muted-foreground">{t("onboarding_complete_data_types")}</p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-primary" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col items-center justify-center p-6 space-y-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold mb-1">{t("onboarding_complete_insights_ready")}</h3>
              <p className="text-2xl font-bold text-primary">✓</p>
              <p className="text-sm text-muted-foreground">{t("onboarding_complete_ai_enabled")}</p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-primary" />
          </CardContent>
        </Card>
      </div>

      {/* What's Next */}
      <Card className="text-left">
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold text-lg">{t("onboarding_complete_whats_next")}</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            {[
              t("onboarding_complete_next_1"),
              t("onboarding_complete_next_2"),
              t("onboarding_complete_next_3"),
              t("onboarding_complete_next_4"),
            ].map((text, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs text-primary">{i + 1}</span>
                </div>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Complete Button */}
      <div className="pt-8">
        <Button
          size="lg"
          onClick={onComplete}
          className="w-full md:w-auto px-12 h-12 text-base"
        >
          {t("onboarding_complete_go_dashboard")}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
